"use client"

import { useFormik, FormikProvider } from "formik"
import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { FormInput, FormSelect } from "@/components/form-inputs"
import { useSavePartnerCodeMutation } from "@/hooks/mutations/partners"
import { getBONUS_LIMIT_OPTIONS, bonusLimitCountError, bonusLimitFrom, bonusLimitModeFor, bonusPercentError, parseBonusPercent, type BonusLimitMode, type PartnerBonusTranslator } from "@/components/partners/client-bonus"
import { formatPercent } from "@/lib/partners/percent"
import { OptionalPromoCodeSchema, REFERRAL_CODE_LENGTH } from "@/lib/partners/promo-code"
import type { PartnerCodeView } from "@/server-actions/partners"
import { useTranslations } from "next-intl"



type PartnerCodeForm = {
  label: string
  code: string
  bonusPercent: string
  limitMode: BonusLimitMode
  limitCount: string
}

export function PartnerCodeDialog({
  open,
  code,
  partnerPercent,
  onOpenChange,
}: {
  open: boolean
  /** Null when creating a new link. */
  code: PartnerCodeView | null
  /** The partner's own percent — what a brand-new code accrues at. */
  partnerPercent: number
  onOpenChange: (open: boolean) => void
}) {
  const tBonus = useTranslations("PartnerBonus")
  const BONUS_LIMIT_OPTIONS = getBONUS_LIMIT_OPTIONS(useTranslations("Metadata.PartnersClientBonus"))


  const tUi = useTranslations("Partners.PartnersPartnerCodeDialog")

  const mutation = useSavePartnerCodeMutation({ setErrors: errors => formik.setErrors(errors) })
  const effectivePercent = code?.commission_percent ?? partnerPercent

  const formik = useFormik<PartnerCodeForm>({
    enableReinitialize: true,
    initialValues: initialValuesFor(code),
    validate: values => validate(values, effectivePercent, tBonus),
    onSubmit: async values => {
      try {
        await mutation.mutateAsync({
          id: code?.id,
          code: code ? undefined : values.code,
          label: values.label.trim() || null,
          client_bonus_percent: parseBonusPercent(values.bonusPercent),
          client_bonus_payment_limit: bonusLimitFrom(values.limitMode, values.limitCount),
        })
        onOpenChange(false)
      } catch {
        // withQueryHandling already surfaced the error.
      }
    },
  })

  const bonusPercent = parseBonusPercent(formik.values.bonusPercent)
  const partnerShare = Math.max(0, effectivePercent - bonusPercent)

  return (
    <Dialog open={open} onOpenChange={next => !mutation.isPending && onOpenChange(next)}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <DialogHeader>
              <DialogTitle><span key={code ? tUi("editReferralLink") : tUi("newReferralLink")}>{code ? tUi("editReferralLink") : tUi("newReferralLink")}</span></DialogTitle>
              <DialogDescription>
                <span key={code
                  ? tUi("linkCodeValue0TheCodeItselfNeverChanges", { value0: String(code.code) })
                  : tUi("chooseYourOwnPromoCodeOrLeaveIt")}>{code
                  ? tUi("linkCodeValue0TheCodeItselfNeverChanges", { value0: String(code.code) })
                  : tUi("chooseYourOwnPromoCodeOrLeaveIt")}</span>
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-5">
              <FormInput
                name="label"
                label={tUi("name")}
                placeholder={tUi("newsletterYouTube")}
                maxLength={80}
                autoFocus
              />
              {!code && (
                <FormInput
                  name="code"
                  label={tUi("promoCodeOptional")}
                  placeholder={"NEWS2026"}
                  maxLength={REFERRAL_CODE_LENGTH}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  description={tUi("leaveBankToGenerateAutomaticallyMustBeValue0", { value0: String(REFERRAL_CODE_LENGTH) })}
                />
              )}
              <FormInput
                name="bonusPercent"
                label={tUi("clientBonus")}
                inputMode="decimal"
                description={tUi("paidOutOfYourOwnValue0CommissionAs", { value0: String(formatPercent(effectivePercent)) })}
              />
              <FormSelect<BonusLimitMode>
                name="limitMode"
                label={tUi("bonusAppliesTo")}
                options={BONUS_LIMIT_OPTIONS}
              />
              {formik.values.limitMode === "limited" && (
                <FormInput name="limitCount" label={tUi("numberOfPayments")} inputMode="numeric" />
              )}
              <p className="text-sm text-muted-foreground">
                <span>{<span key={tUi("youKeepValue0", { value0: String(formatPercent(partnerShare)) })}>{tUi("youKeepValue0", { value0: String(formatPercent(partnerShare)) })}</span>}</span>
                {bonusPercent > 0 ? (
                  <span>{<span key={tUi("theClientGetsValue0BackToTheirWallet", { value0: String(formatPercent(bonusPercent)) })}>{tUi("theClientGetsValue0BackToTheirWallet", { value0: String(formatPercent(bonusPercent)) })}</span>}</span>
                ) : null}
              </p>
            </div>

            <DialogFooter>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                <span key={code ? tUi("save") : tUi("createLink")}>{code ? tUi("save") : tUi("createLink")}</span>
              </Button>
            </DialogFooter>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}

function initialValuesFor(code: PartnerCodeView | null): PartnerCodeForm {
  const limit = code?.client_bonus_payment_limit ?? null

  return {
    label: code?.label ?? "",
    code: "",
    bonusPercent: code ? String(code.client_bonus_percent) : "0",
    limitMode: bonusLimitModeFor(limit),
    limitCount: limit && limit > 1 ? String(limit) : "2",
  }
}

function validate(values: PartnerCodeForm, effectivePercent: number, tBonus: PartnerBonusTranslator) {
  const errors: Partial<Record<keyof PartnerCodeForm, string>> = {}

  const promoCode = OptionalPromoCodeSchema.safeParse(values.code)
  if (!promoCode.success) {
    const issue = promoCode.error.issues[0]
    const reason = issue.code === "custom" ? issue.params?.reason : undefined
    switch (reason) {
      case "code_length": errors.code = tBonus("promoCodeLength", {length: REFERRAL_CODE_LENGTH}); break
      case "code_characters": errors.code = tBonus("promoCodeCharacters"); break
      default: errors.code = tBonus("invalidPromoCode")
    }
  }

  const bonusError = bonusPercentError(
    values.bonusPercent,
    effectivePercent,
    tBonus("partnerCeiling", {percent: formatPercent(effectivePercent)}),
    tBonus,
  )
  if (bonusError) errors.bonusPercent = bonusError

  if (values.limitMode === "limited") {
    const limitError = bonusLimitCountError(values.limitCount, tBonus)
    if (limitError) errors.limitCount = limitError
  }

  return errors
}
